<template>
  <div>
    <b-modal
      ref="modalRef"
      centered
      :title="hasEmailEvents ? $t('DEBTIA_EMAIL_LOGS_ACTIVITY') : $t('DEBTIA_LETTER_LOGS_ACTIVITY')"
      size="md"
      :no-close-on-backdrop="true"
      ok-title="Download PDF"
      outline-variant="secondary"
      :ok-disabled="loader"
      @ok="DownloadPDF"
    >
      <b-overlay
        id="overlay-background"
        :show="loader"
        variant="dark"
        opacity="0.85"
        blur="2px"
        rounded="sm"
        spinner-variant="primary"
        spinner-medium
      >
        <b-card
          v-if="hasEmailEvents"
          class="border"
          title="Details"
        >
          <div
            v-for="(log, index) in caseLogsData"
            :key="'email-detail-'+index"
          >
            <div
              v-if="log.case_email_logs"
              class="border mb-10 padding-content"
            >
              <div class="d-flex align-items-center justify-content-between">
                <label class="note-font">
                  {{ $t('DEBTIA_TO') }}: {{ getReceiver(log.case_email_logs.receiver) }}
                </label>
              </div>
            </div>
            <div
              v-if="log.case_email_logs"
              class="border mb-10 padding-content"
            >
              <div class="d-flex align-items-center justify-content-between">
                <label class="note-font">
                  {{ $t('DEBTIA_FROM') }}: {{ log.case_email_logs.sender || '-' }}
                </label>
              </div>
            </div>
            <div
              v-if="log.case_email_logs"
              class="border mb-10 padding-content"
            >
              <div class="d-flex align-items-center justify-content-between">
                <label
                  class="note-font"
                >
                  {{ $t('DEBTIA_SUBJECT') }}: {{ log.case_email_logs.subject || '-' }}
                </label>
              </div>
            </div>
          </div>
        </b-card>

        <b-card
          v-if="hasEmailEvents"
          class="border"
          title="Event History"
        >
          <app-timeline
            v-for="(log, index) in caseLogsData"
            :key="'email-'+index"
          >
            <app-timeline-item
              v-for="(event, eIndex) in getEmailEvents(log)"
              :key="eIndex"
              :icon="getEventInfo(event.event_type).icon"
              :variant="getEventInfo(event.event_type).variant"
            >
              <div class="d-flex justify-content-between">
                <h6>{{ getEventInfo(event.event_type).label }}</h6>
                <small class="text-muted">
                  {{ $i18n.locale === 'en' ? event.en_date_format : event.da_date_format }}
                </small>
              </div>
            </app-timeline-item>
          </app-timeline>
        </b-card>

        <!-- LETTER EVENTS -->
        <b-card
          v-if="hasLetterEvents"
          class="border"
          title="Details"
        >
          <div
            v-for="(log, index) in caseLogsData"
            :key="'email-detail-'+index"
          >
            <div
              v-if="log.case_letter_logs"
              class="border mb-10 padding-content"
            >
              <div class="d-flex align-items-center justify-content-between">
                <label class="note-font">
                  {{ $t('DEBTIA_TO') }}: {{ getReceiver(log.case_letter_logs.receiver) }}
                </label>
              </div>
            </div>
            <div
              v-if="log.case_letter_logs"
              class="border mb-10 padding-content"
            >
              <div class="d-flex align-items-center justify-content-between">
                <label class="note-font">
                  {{ $t('DEBTIA_FROM') }}: {{ log.case_letter_logs.sender || '-' }}
                </label>
              </div>
            </div>
            <div
              v-if="log.case_letter_logs"
              class="border mb-10 padding-content"
            >
              <div class="d-flex align-items-center justify-content-between">
                <label
                  class="note-font"
                >
                  {{ $t('DEBTIA_SUBJECT') }}: {{ log.case_letter_logs.subject || '-' }}
                </label>
              </div>
            </div>
          </div>
        </b-card>

        <b-card
          v-if="hasLetterEvents"
          class="border"
        >
          <app-timeline
            v-for="(log, index) in caseLogsData"
            :key="'letter-'+index"
          >
            <app-timeline-item
              v-for="(event, eIndex) in getLetterEvents(log)"
              :key="eIndex"
              :icon="getEventInfo(event.event_type).icon"
              :variant="getEventInfo(event.event_type).variant"
            >
              <div class="d-flex justify-content-between">
                <h6>{{ getEventInfo(event.event_type).label }}</h6>
                <small class="text-muted">
                  {{ $i18n.locale === 'en' ? event.en_date_format : event.da_date_format }}
                </small>
              </div>
            </app-timeline-item>
          </app-timeline>
        </b-card>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal,
  BCard,
  BOverlay,
} from 'bootstrap-vue'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'

export default {
  components: {
    BModal,
    BCard,
    BOverlay,
    AppTimeline,
    AppTimelineItem,
  },
  props: {
    caseLogsData: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      loader: false,
      EVENT_MAP: {
        delivered: { label: 'Delivered', icon: 'SendIcon', variant: 'success' },
        open: { label: 'Opened', icon: 'InboxIcon', variant: 'info' },
        processed: { label: 'Processed', icon: 'RefreshCcwIcon', variant: 'primary' },
        bounce: { label: 'Bounced', icon: 'AlertCircleIcon', variant: 'danger' },
        dropped: { label: 'Dropped', icon: 'XIcon', variant: 'warning' },
      },
      DEFAULT_EVENT: { icon: 'HelpCircleIcon', variant: 'danger' },
    }
  },
  computed: {
    hasEmailEvents() {
      return this.caseLogsData.some(log => log.case_email_logs && log.case_email_logs.email_events && log.case_email_logs.email_events.length)
    },
    hasLetterEvents() {
      return this.caseLogsData.some(log => log.case_letter_logs && log.case_letter_logs.email_events && log.case_letter_logs.email_events.length)
    },
  },
  methods: {
    show() {
      this.$refs.modalRef.show()
    },
    hide() {
      this.$refs.modalRef.hide()
    },
    getReceiver(str) {
      try {
        const obj = JSON.parse(str)
        return obj.to || '-'
      } catch {
        return '-'
      }
    },
    getEmailEvents(log) {
      return log.case_email_logs && log.case_email_logs.email_events ? log.case_email_logs.email_events : []
    },
    getLetterEvents(log) {
      return log.case_letter_logs && log.case_letter_logs.email_events ? log.case_letter_logs.email_events : []
    },
    getEventInfo(type) {
      if (!type) return null
      const normalizedType = type.toLowerCase()
      return this.EVENT_MAP[normalizedType] ?? {
        label: normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1),
        ...this.DEFAULT_EVENT,
      }
    },
    DownloadPDF() {
      if (!this.caseLogsData.length) {
        return
      }

      this.loader = true
      const url = `/eventlog/get-sendgrid-log/${this.caseLogsData[0].id}`

      axios.get(url)
        .then(response => {
          const pdfurl = response.data?.data?.pdf_url
          if (!pdfurl) return

          const link = document.createElement('a')
          link.href = pdfurl
          link.target = '_blank'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        })
        .catch(err => {
          this.loader = false
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              icon: 'CoffeeIcon',
              variant: 'danger',
              text: err.response?.data?.message || 'Something went wrong',
            },
          })
        })
        .finally(() => {
          this.loader = false
        })
    },

  },
}
</script>
